/**
 * PM fee rules: separate from the CPI, stored per request row, never folded in.
 *
 * Must NEVER import `@repo/db`: "use client" components import this, and a db
 * value-import breaks `next build` while `type-check` stays green.
 */
import { z } from "zod";

import { DECIMAL_AMOUNT_REGEX, decimalAmountSchema } from "./schemas";

// Re-exported so client-safe consumers reach the regex through this subpath.
// Importing it from the root barrel would drag the barrel's db-backed modules
// into a jest run that only wants the fee helpers.
export { DECIMAL_AMOUNT_REGEX };

export const pmFeeBasisSchema = z.enum(["PROJECT", "PER_EXPERT"]);

// Not "FLAT": `platformFeeType.FLAT` already means per participant, the inverse.
export type PmFeeBasis = z.infer<typeof pmFeeBasisSchema>;

export interface PmFee {
  amount: number;
  basis: PmFeeBasis;
}

// Both fields together or neither; exactly one half is always rejected.
export const pmFeeInputSchema = z
  .object({
    pmFee: decimalAmountSchema.nullable().optional(),
    pmFeeBasis: pmFeeBasisSchema.nullable().optional(),
  })
  .superRefine((value, ctx) => {
    const amountSet = value.pmFee !== undefined && value.pmFee !== null;
    const basisSet =
      value.pmFeeBasis !== undefined && value.pmFeeBasis !== null;
    if (amountSet === basisSet) {
      return;
    }
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "pmFee and pmFeeBasis must be set or cleared together",
      path: [amountSet ? "pmFeeBasis" : "pmFee"],
    });
  });

// The one gate for "no fee": a half-set pair, a non-numeric amount, NULL and
// "0.00" all read as null.
export function readPmFee(
  pmFee: string | null,
  basis: PmFeeBasis | null,
): PmFee | null {
  if (pmFee === null || basis === null) {
    return null;
  }
  const amount = Number(pmFee);
  if (!Number.isFinite(amount) || amount <= 0) {
    return null;
  }
  return { amount, basis };
}

/** Quote-facing: `3000 -> "$3,000"`, `2500.5 -> "$2,500.50"`. */
export function formatPmFeeAmount(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

/** "$3,000 for the project" | "$50 per participant". */
export function formatPmFee(fee: PmFee): string {
  const amount = formatPmFeeAmount(fee.amount);
  return fee.basis === "PROJECT"
    ? `${amount} for the project`
    : `${amount} per participant`;
}

/** A rendered label plus the indices, into the caller's array, that share it. */
export interface PmFeeLine {
  label: string;
  requestIndices: number[];
}

// The invariant, owned here: a PROJECT fee replicated across sibling market
// rows is still ONE fee. Use one line per distinct value and never add the amounts:
// $3,000 on 12 rows is $3,000, not $36,000.
export function collapsePmFees(fees: (PmFee | null)[]): PmFeeLine[] {
  const groups = new Map<string, { fee: PmFee; requestIndices: number[] }>();
  fees.forEach((fee, index) => {
    if (fee === null) {
      return;
    }
    const key = `${fee.basis}:${fee.amount}`;
    const existing = groups.get(key);
    if (existing) {
      existing.requestIndices.push(index);
    } else {
      groups.set(key, { fee, requestIndices: [index] });
    }
  });
  return Array.from(groups.values()).map(({ fee, requestIndices }) => ({
    label: formatPmFee(fee),
    requestIndices,
  }));
}

/**
 * Two different problems, and only one of them depends on the basis.
 *
 * Distinct values across the group always disagree. Unpriced rows only do when
 * a priced row is PER_EXPERT: a per-participant fee has to sit on every market
 * it applies to, so a blank one is a market someone forgot. A PROJECT fee is a
 * statement about the whole project, so one row carrying it says everything;
 * flagging the other rows would push the operator to retype the same number
 * per market for no change in output, since collapsePmFees folds it back to a
 * single line either way.
 */
export function hasPmFeeMismatch(fees: (PmFee | null)[]): boolean {
  const priced = fees.filter((fee): fee is PmFee => fee !== null);
  if (priced.length === 0) {
    return false;
  }
  if (collapsePmFees(fees).length > 1) {
    return true;
  }
  return (
    priced.length < fees.length &&
    priced.some((fee) => fee.basis === "PER_EXPERT")
  );
}
